import { deleteSession, SchoolClassTypeEnum } from "@/services/internalApiV1";
import { isPupil, type RefreshResult } from "@/stores/user";

export const useSession = (): {
  logout: (redirectBack?: false | string) => Promise<void>;
  refetchUser: () => Promise<RefreshResult>;
} => {
  const userStore = useUserStore();
  const incognitoStore = useIncognitoStore();

  const queryClient = useQueryClient();

  const refetchUser = (): Promise<RefreshResult> => userStore.refresh();

  const logout = async (
    redirectBack: false | string = false
  ): Promise<void> => {
    try {
      const pupil = userStore.isPupil;
      let isOneTime = false;
      if (isPupil(userStore.user)) {
        isOneTime =
          userStore.user?.schoolClassType === SchoolClassTypeEnum.ONETIME;
      }

      await deleteSession();

      userStore.reset();
      incognitoStore.$reset();

      queryClient.clear();

      // Don't refetch user after logout to avoid race conditions
      // The store is now cleared and isAuthenticated will correctly return false
      // If a visitor session is needed, it will be fetched lazily when required

      // Wait for Vue reactivity to settle and ensure all stores are properly updated
      // This prevents race conditions where the login page might check authentication
      // before the store updates have fully propagated
      await nextTick();

      if (!redirectBack) {
        // Full page load tears down the shared cable (services/internalCableClient.ts); SPA navigation would leak WebSockets across sessions.
        if (pupil) {
          if (isOneTime) {
            window.location.replace("/p/logged_out");
          } else {
            window.location.replace("/go");
          }
        } else {
          window.location.replace("/users/sign_in");
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    logout,
    refetchUser,
  };
};
